import React, { useState } from "react";
import { FolderPlus, Loader2 } from "lucide-react";
import { foldersApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  parentId?: string;
  clientId?: string;
  matterId?: string;
}

const CreateFolderModal: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
  parentId,
  clientId,
  matterId,
}) => {
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Folder name is required");
      return;
    }

    setCreating(true);
    setError("");

    try {
      await foldersApi.create({
        name: name.trim(),
        parentId: parentId || undefined,
        clientId: clientId || undefined,
        matterId: matterId || undefined,
      });
      setName("");
      onCreated();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to create folder");
    } finally {
      setCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !creating) {
      handleSubmit();
    }
  };

  const footerContent = (
    <div className="flex items-center justify-end gap-3">
      <button
        onClick={onClose}
        disabled={creating}
        className="px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        onClick={handleSubmit}
        disabled={creating || !name.trim()}
        className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50"
        style={{ backgroundColor: "var(--color-primary)" }}
        onMouseEnter={(e) => {
          if (!creating) e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
        }}
        onMouseLeave={(e) => {
          if (!creating) e.currentTarget.style.backgroundColor = "var(--color-primary)";
        }}
      >
        {creating ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Creating...
          </>
        ) : (
          <>
            <FolderPlus className="h-4 w-4" />
            Create
          </>
        )}
      </button>
    </div>
  );

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      title="New Folder"
      icon={FolderPlus}
      size="sm"
      footer={footerContent}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Folder Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter folder name"
            autoFocus
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </PremiumModal>
  );
};

export default CreateFolderModal;
